/**
 * The respondent-facing brand header: the org logo (the survey's public
 * snapshot) plus one optional muted line. ONE implementation for every
 * branded respondent screen — the form view (with the line above the title),
 * the thank-you screen (logo) and the closed-survey page.
 *
 * SECURITY — `logoUrl` is server-built (the public bucket URL of the API's
 * sniffed PNG / JPEG / WebP copy); `line` is rendered as a React text node.
 * A logo that fails to load (removed object, offline) is hidden, never a
 * broken-image icon.
 */
import { useState } from 'react';
import { useTranslation } from '@/hooks/useTranslation';
import { cn } from '@/lib/utils';
import type { SurveyIntroLine } from '../surveyLabels';

export interface SurveyBrandHeaderProps {
  orgName: string;
  /** The survey's logo snapshot (public URL); NULL / absent → no logo. */
  logoUrl?: string | null;
  /**
   * One small muted line under the logo (the line above the title, from
   * `surveyIntroLine`). The DEFAULT line is an uppercase eyebrow; a custom
   * line is shown exactly as typed (no case transform, no tracking).
   */
  line?: SurveyIntroLine | null;
  /** The logo repeats a logo already announced (the thank-you screen): `alt=""`. */
  decorativeLogo?: boolean;
  /** Centre the logo (the thank-you and closed screens). */
  centered?: boolean;
  className?: string;
}

export function SurveyBrandHeader({
  orgName,
  logoUrl,
  line,
  centered = false,
  decorativeLogo = false,
  className,
}: SurveyBrandHeaderProps) {
  const { t } = useTranslation('surveys');
  const [failedLogoUrl, setFailedLogoUrl] = useState<string | null>(null);
  const showLogo = !!logoUrl && logoUrl !== failedLogoUrl;
  if (!showLogo && !line?.text) return null;
  return (
    <div className={cn('space-y-2', className)}>
      {showLogo && (
        <img
          src={logoUrl}
          alt={decorativeLogo ? '' : t('public.logoAlt', { org: orgName })}
          className={cn('mb-4 max-h-12 w-auto object-contain', centered && 'mx-auto')}
          onError={() => setFailedLogoUrl(logoUrl)}
        />
      )}
      {line?.text && (
        <p
          className={cn(
            'text-xs font-semibold text-muted-foreground',
            !line.custom && 'uppercase tracking-wider',
          )}
        >
          {line.text}
        </p>
      )}
    </div>
  );
}
